import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import debounce from 'lodash.debounce';
import { List, useListRef } from 'react-window';
import type { RowComponentProps } from 'react-window';
import {
  Box,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Paper,
  Tooltip,
  ToggleButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  BugReport as DebugIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Clear as ClearIcon,
  KeyboardArrowUp as PrevMarkIcon,
  KeyboardArrowDown as NextMarkIcon,
  DragHandle as DragHandleIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import type { DeviceLog, LogLevel, LogType } from '@/types';
import { colors } from '@/theme/tokens';

// --- Level config ---
const levelConfig: Record<LogLevel, { darkBg: string; color: string; icon: ReactElement }> = {
  debug: { darkBg: '#2A2A2A', color: '#757575', icon: <DebugIcon sx={{ fontSize: 13 }} /> },
  info: { darkBg: '#1A2733', color: '#1976D2', icon: <InfoIcon sx={{ fontSize: 13 }} /> },
  warning: { darkBg: '#332A1A', color: '#F57C00', icon: <WarningIcon sx={{ fontSize: 13 }} /> },
  error: { darkBg: '#331A1A', color: '#D32F2F', icon: <ErrorIcon sx={{ fontSize: 13 }} /> },
  fatal: { darkBg: '#4D1A1A', color: '#B71C1C', icon: <ErrorIcon sx={{ fontSize: 13 }} /> },
};

const logTypeLabels: Record<LogType, string> = {
  system: 'SYS', app: 'APP', crash: 'CRASH', network: 'NET',
  security: 'SEC', command: 'CMD', activity: 'ACT', kernel: 'KERN',
};

const ROW_HEIGHT = 24;
const MIN_DETAIL_HEIGHT = 80;
const MAX_DETAIL_HEIGHT = 500;

// Pre-compute timestamp strings
const timestampCache = new Map<string, string>();
function getTimestamp(isoStr: string): string {
  let ts = timestampCache.get(isoStr);
  if (!ts) {
    ts = new Date(isoStr).toLocaleTimeString('en-US', {
      hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    timestampCache.set(isoStr, ts);
    if (timestampCache.size > 10000) {
      const first = timestampCache.keys().next().value;
      if (first) timestampCache.delete(first);
    }
  }
  return ts;
}

function highlightText(text: string, regex: RegExp | null): React.ReactNode {
  if (!regex) return text;
  const parts = text.split(regex);
  if (parts.length <= 1) return text;
  const matches = text.match(regex);
  if (!matches) return text;
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && matches[i] && (
        <mark style={{ background: '#5A4A00', color: '#FFD700', borderRadius: 2, padding: '0 1px' }}>
          {matches[i]}
        </mark>
      )}
    </span>
  ));
}

// --- Column header ---
function ColumnHeader() {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', height: 28, px: 0.5,
        borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover',
        fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: 'text.secondary',
        userSelect: 'none', flexShrink: 0,
      }}
    >
      <Box sx={{ width: 36, textAlign: 'center', flexShrink: 0 }}>#</Box>
      <Box sx={{ width: 20, textAlign: 'center', flexShrink: 0 }}>M</Box>
      <Box sx={{ width: 58, flexShrink: 0 }}>TIME</Box>
      <Box sx={{ width: 56, flexShrink: 0 }}>LEVEL</Box>
      <Box sx={{ width: 40, flexShrink: 0 }}>TYPE</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>MESSAGE</Box>
    </Box>
  );
}

// --- Log row ---
//
// IMPORTANT: do NOT include the full logs array in this prop bag.
// react-window spreads rowProps onto every row fiber's memoizedProps;
// React 19's dev render profiler diffs memoizedProps on every render,
// then structured-clones the diff result into performance.measure's
// detail. With ~60 visible+overscan rows × 5000-item array clone per
// tick the tab OOMs. Use the getLog callback (stable identity, returns
// the latest displayLogs slice via ref) so each row's memoizedProps
// stays tiny.
interface LogRowProps {
  getLog: (index: number) => DeviceLog | undefined;
  selectedIndex: number | null;
  markedIds: Set<string>;
  onSelect: (index: number) => void;
  onToggleMark: (id: string) => void;
  searchRegex: RegExp | null;
}

function LogRowRenderer(props: RowComponentProps<LogRowProps>) {
  const { index, style, getLog, selectedIndex, markedIds, onSelect, onToggleMark, searchRegex } = props;
  const log = getLog(index);
  // Defensive: if the ref hasn't caught up to a transient out-of-range
  // index (e.g. row N rendered while the array shrank below N+1), render
  // an empty placeholder rather than crashing the row.
  if (!log) {
    return <div style={style} />;
  }
  const config = levelConfig[log.level] || levelConfig.info;
  const isSelected = selectedIndex === index;
  const isMarked = markedIds.has(log.id);
  const timestamp = getTimestamp(log.created_at);

  // Synthetic pause-gap markers (injected when the user toggles live
  // logging off → on) render as a compact centered divider note instead
  // of a full log row — they're metadata about the viewer, not a real
  // log line, so they shouldn't fight for visual weight with actual logs.
  if (log.metadata && (log.metadata as Record<string, unknown>).kind === 'pause_gap') {
    return (
      <div
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingLeft: 8,
          paddingRight: 8,
          fontFamily: 'monospace',
          fontSize: 10,
          fontStyle: 'italic',
          color: colors.text.tertiary,
          backgroundColor: colors.background.tableRowAlt,
          borderTop: `1px dashed ${colors.border.light}`,
          borderBottom: `1px dashed ${colors.border.light}`,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
        title={log.message}
      >
        {log.message}
      </div>
    );
  }

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 4,
        paddingRight: 4,
        cursor: 'pointer',
        fontFamily: 'monospace',
        fontSize: 11,
        lineHeight: `${ROW_HEIGHT}px`,
        backgroundColor: isSelected
          ? 'rgba(0,0,0,0.08)'
          : isMarked
          ? 'rgba(255,103,32,0.08)'
          : log.level === 'error' || log.level === 'fatal'
          ? 'rgba(230,10,50,0.05)'
          : log.level === 'warning'
          ? 'rgba(243,167,46,0.06)'
          : undefined,
        overflow: 'hidden',
      }}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onClick={() => onSelect(index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(index);
        }
      }}
    >
      <span style={{ width: 36, textAlign: 'right', paddingRight: 8, color: colors.text.tertiary, flexShrink: 0, fontSize: 10 }}>
        {index + 1}
      </span>
      <span
        onClick={(e) => { e.stopPropagation(); onToggleMark(log.id); }}
        style={{
          width: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, color: isMarked ? colors.primary.main : 'transparent', cursor: 'pointer',
        }}
      >
        {isMarked ? '\u2691' : '\u2690'}
      </span>
      <span style={{ width: 58, flexShrink: 0, color: colors.text.tertiary }}>{timestamp}</span>
      <span style={{ width: 56, flexShrink: 0, color: config.color, fontWeight: 600 }}>
        {log.level.toUpperCase().slice(0, 4)}
      </span>
      <span style={{ width: 40, flexShrink: 0, color: colors.text.tertiary }}>
        {logTypeLabels[log.log_type] || log.log_type}
      </span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {highlightText(log.message, searchRegex)}
      </span>
    </div>
  );
}

// --- Detail Panel ---
function DetailPanel({ log, onClose }: { log: DeviceLog; onClose: () => void }) {
  const config = levelConfig[log.level] || levelConfig.info;
  const fullTimestamp = new Date(log.created_at).toLocaleString();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
          borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>DETAIL</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
          <Box sx={{ px: 0.75, py: 0.25, borderRadius: 0.5, fontSize: 10, fontWeight: 600, fontFamily: 'monospace', bgcolor: config.darkBg, color: config.color }}>
            {log.level.toUpperCase()}
          </Box>
          <Box sx={{ px: 0.75, py: 0.25, borderRadius: 0.5, fontSize: 10, fontFamily: 'monospace', color: 'text.secondary', border: '1px solid', borderColor: 'divider' }}>
            {logTypeLabels[log.log_type] || log.log_type}
          </Box>
        </Box>
        <Typography sx={{ fontSize: 10, color: 'text.disabled', ml: 1 }}>{fullTimestamp}</Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25 }}>
          <IconButton size="small" onClick={() => navigator.clipboard.writeText(log.message)} title="Copy message">
            <CopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton size="small" onClick={onClose} title="Close detail">
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        <Paper
          variant="outlined"
          sx={{ p: 1, mb: 1, fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 200, overflow: 'auto', bgcolor: 'background.default' }}
        >
          {log.message}
        </Paper>
        <Box sx={{ display: 'flex', gap: 2, fontSize: 10, color: 'text.disabled' }}>
          <span>Device: {log.device_id}</span>
          <span>ID: {log.id}</span>
        </Box>
      </Box>
    </Box>
  );
}

// --- Resize Handle ---
function ResizeHandle({ onDrag }: { onDrag: (deltaY: number) => void }) {
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // Emit the INCREMENTAL delta (movement since the previous event), not the
      // cumulative offset from drag start — handleResize accumulates onto a ref
      // that updates every frame, so a cumulative delta compounds and the panel
      // accelerates/overshoots the cursor (the "not smooth" resize).
      let lastY = e.clientY;
      const handleMouseMove = (moveEvent: MouseEvent) => {
        onDrag(moveEvent.clientY - lastY);
        lastY = moveEvent.clientY;
      };
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [onDrag]
  );

  return (
    <Box
      onMouseDown={handleMouseDown}
      sx={{
        height: 6, cursor: 'row-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: 'action.hover', borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
        flexShrink: 0, '&:hover': { bgcolor: 'action.focus' },
      }}
    >
      <DragHandleIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
    </Box>
  );
}

// --- Main LogViewer ---
interface LogViewerProps {
  /** Static log array. Use for small/paginated sources (e.g. DB results).
   *  For large live buffers, use getLogs + logsVersion instead so the
   *  array never enters this component's memoizedProps — React 19's dev
   *  profiler structured-clones changed props on every render and OOMs
   *  past ~10k rows. */
  logs?: DeviceLog[];
  /** Callback-based source for live/large buffers. The full array lives
   *  in a ref upstream; LogViewer re-reads it each time logsVersion
   *  changes and keeps the result in its own useMemo state (which is
   *  not profiled the way props are). */
  getLogs?: () => DeviceLog[];
  /** Monotonic tick from upstream; bump on every buffer mutation. */
  logsVersion?: number;
  /** Small UI-only entries to merge into the live source (e.g. pause-gap
   *  markers). Sorted by created_at alongside the streamed logs. Stays
   *  small (handful of items); safe to pass as a regular prop. */
  extraLogs?: DeviceLog[];
  height?: number;
  onClear?: () => void;
  isLive?: boolean;
  /** Hide the "All Levels" filter dropdown in the toolbar. */
  hideLevelFilter?: boolean;
  /** Hide the "All Types" filter dropdown in the toolbar. */
  hideTypeFilter?: boolean;
}

export function LogViewer({
  logs,
  getLogs,
  logsVersion,
  extraLogs,
  height = 500,
  isLive = false,
  hideLevelFilter = false,
  hideTypeFilter = false,
}: LogViewerProps) {
  // Resolve the effective log array. In live/callback mode we re-pull on
  // every version bump; the resulting array lives in a useMemo (i.e. the
  // fiber's hook state, not memoizedProps) and is therefore not subject
  // to the profiler's structured-clone path.
  const effectiveLogs = useMemo(() => {
    const base = getLogs ? getLogs() : (logs ?? []);
    if (!extraLogs || extraLogs.length === 0) return base;
    const merged = [...base, ...extraLogs];
    merged.sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    return merged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLogs, logsVersion, logs, extraLogs]);

  const [search, setSearch] = useState('');
  const [isRegex, setIsRegex] = useState(false);
  const [levelFilter, setLevelFilter] = useState<LogLevel | ''>('');
  const [typeFilter, setTypeFilter] = useState<LogType | ''>('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [markedIds, setMarkedIds] = useState<Set<string>>(new Set());
  const [detailHeight, setDetailHeight] = useState(180);
  const [showDetail, setShowDetail] = useState(false);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const listRef = useListRef(null);
  const detailHeightRef = useRef(detailHeight);
  // Tracks the most recent visible row range (from react-window's
  // onRowsRendered). Used by: (1) "near oldest → trigger load-older"
  // auto-fetch, (2) "user scrolled back to tail → resume autoscroll",
  // (3) the scroll-position-preservation effect after extraLogs grows.
  const lastStartIndexRef = useRef(0);
  const lastStopIndexRef = useRef(0);
  // Tracks the length of extraLogs across renders so the scroll
  // preservation effect can compute how many rows were prepended.
  const prevExtraLogsLenRef = useRef(extraLogs?.length ?? 0);

  // Mirror isAutoScroll into a ref so the scroll event handler reads the
  // latest value without re-creating the listener on every state flip.
  const isAutoScrollRef = useRef(isAutoScroll);
  isAutoScrollRef.current = isAutoScroll;

  // Suppress the next scroll event when we trigger a programmatic
  // scrollToRow. Programmatic scrolls always INCREASE scrollTop (we only
  // scroll downward toward the tail), so without this flag the listener
  // would not falsely fire — but we still set/clear it for clarity and
  // future-proofing. Also captures lastScrollTop initial value.
  const ignoreScrollRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  // Set when the user jumps to a search match. Suppresses the at-tail
  // auto-resume below so jumping to a match near the bottom doesn't snap
  // the viewport straight back to the live tail. Cleared on the next
  // genuine user scroll (they've taken control of the viewport again).
  const manualJumpRef = useRef(false);
  // Listener attachment is gated on react-window having mounted its inner
  // element. We can't know that from outside, so the first call to
  // handleRowsRendered (post-mount) attaches; cleanup runs on unmount.
  const listenersAttachedRef = useRef(false);
  const cleanupListenersRef = useRef<(() => void) | null>(null);

  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setDebouncedSearch(value), 300),
    []
  );

  useEffect(() => {
    debouncedSetSearch(search);
    return () => debouncedSetSearch.cancel();
  }, [search, debouncedSetSearch]);

  const searchRegex = useMemo(() => {
    if (!debouncedSearch) return null;
    try {
      if (isRegex) return new RegExp(debouncedSearch, 'gi');
      return new RegExp(debouncedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    } catch {
      return null;
    }
  }, [debouncedSearch, isRegex]);

  // Level/type are hard filters (remove non-matching rows); search is NOT
  // — it only highlights within rows so the user keeps surrounding context
  // (terminal / grep-inside-less behavior). matchCount in the status bar
  // tells them how many rows match the active search.
  const filteredLogs = useMemo(() => {
    return effectiveLogs.filter((log) => {
      if (levelFilter && log.level !== levelFilter) return false;
      if (typeFilter && log.log_type !== typeFilter) return false;
      return true;
    });
  }, [effectiveLogs, levelFilter, typeFilter]);

  // Always render newest-last (terminal / tail -f feel). Upstream logs
  // arrive newest-first (index 0), so reverse once for display.
  const displayLogs = useMemo(() => [...filteredLogs].reverse(), [filteredLogs]);
  const autoscrollIndex = Math.max(0, displayLogs.length - 1);

  // Row indices (into displayLogs) whose message matches the active
  // search. Drives both the match counter and the ^/v jump-to-match
  // buttons. RegExp is re-created per row because a /g regex carries
  // lastIndex across .test() calls and would otherwise skip matches.
  const matchIndices = useMemo(() => {
    if (!searchRegex) return [];
    const indices: number[] = [];
    for (let i = 0; i < displayLogs.length; i++) {
      const re = new RegExp(searchRegex.source, searchRegex.flags);
      if (re.test(displayLogs[i].message)) indices.push(i);
    }
    return indices;
  }, [displayLogs, searchRegex]);
  const matchCount = matchIndices.length;

  const toggleMark = useCallback((id: string) => {
    setMarkedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleSelect = useCallback((index: number) => {
    setSelectedIndex(index);
    setShowDetail(true);
  }, []);

  const handleCloseDetail = useCallback(() => setShowDetail(false), []);

  const navigateMarks = useCallback(
    (direction: 'prev' | 'next') => {
      const markedIndices = displayLogs
        .map((log, i) => (markedIds.has(log.id) ? i : -1))
        .filter((i) => i >= 0);
      if (markedIndices.length === 0) return;
      const current = selectedIndex ?? -1;
      let target: number;
      if (direction === 'next') {
        target = markedIndices.find((i) => i > current) ?? markedIndices[0];
      } else {
        target = [...markedIndices].reverse().find((i) => i < current) ?? markedIndices[markedIndices.length - 1];
      }
      setSelectedIndex(target);
      setShowDetail(true);
      listRef.current?.scrollToRow({ index: target, align: 'smart' });
    },
    [displayLogs, markedIds, selectedIndex, listRef]
  );

  // Jump the viewport to the previous/next row containing a search match.
  // Mirrors navigateMarks but keyed off the live search instead of
  // bookmarks. Selecting the row highlights it (and the matched text is
  // already <mark>-ed); pausing autoscroll keeps the match in view instead
  // of the next live tick yanking the viewport back to the tail.
  const navigateMatches = useCallback(
    (direction: 'prev' | 'next') => {
      if (matchIndices.length === 0) return;
      const current = selectedIndex ?? -1;
      let target: number;
      if (direction === 'next') {
        target = matchIndices.find((i) => i > current) ?? matchIndices[0];
      } else {
        target =
          [...matchIndices].reverse().find((i) => i < current) ??
          matchIndices[matchIndices.length - 1];
      }
      setSelectedIndex(target);
      // Pin the viewport on the match: stop following the live tail, and
      // flag the jump so handleRowsRendered's at-tail auto-resume doesn't
      // re-enable follow when the match sits near the bottom. 'instant'
      // keeps it to a single scroll event (suppressed via ignoreScrollRef).
      setIsAutoScroll(false);
      manualJumpRef.current = true;
      ignoreScrollRef.current = true;
      listRef.current?.scrollToRow({ index: target, align: 'center', behavior: 'instant' });
    },
    [matchIndices, selectedIndex, listRef]
  );

  const handleResize = useCallback((deltaY: number) => {
    const newHeight = Math.max(MIN_DETAIL_HEIGHT, Math.min(MAX_DETAIL_HEIGHT, detailHeightRef.current - deltaY));
    detailHeightRef.current = newHeight;
    setDetailHeight(newHeight);
  }, []);

  // Autoscroll tick. Driven by logsVersion (not effectiveLogs.length) so
  // we still scroll when the in-memory buffer is at cap and new logs
  // shift content WITHOUT growing the array — that's the case where the
  // viewport would otherwise fall behind, leaving a blank stripe with
  // rows piled at the bottom (the bug visible in earlier screenshots).
  // Falls back to length-delta detection when logsVersion isn't wired
  // (static `logs` prop path).
  const liveTick = logsVersion ?? effectiveLogs.length;
  useEffect(() => {
    if (!isLive || !isAutoScroll) return;
    // align: 'end' pins the newest row to the bottom of the viewport
    // every tick. Required because default 'auto' becomes a no-op as
    // soon as the row is "barely visible" and lets the viewport drift.
    ignoreScrollRef.current = true;
    listRef.current?.scrollToRow({ index: autoscrollIndex, align: 'end' });
     
  }, [liveTick, isAutoScroll, isLive, listRef, autoscrollIndex]);

  // onRowsRendered fires whenever the visible row range changes (scroll
  // or data update). Two jobs:
  // Resume autoscroll when the user has scrolled back to the tail
  // (silent — no "N new" pill). The OFF path is handled by the
  // scroll-event listener attached below: scrollTop decreasing is
  // the only reliable signal that the user actively scrolled up,
  // since onRowsRendered's stopIndex can lag autoscrollIndex during
  // data bursts (was causing autoscroll to spuriously flip off a
  // few seconds into Start Live).
  const TAIL_RESUME_TOLERANCE = 3;
  const handleRowsRendered = useCallback(
    ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
      lastStartIndexRef.current = startIndex;
      lastStopIndexRef.current = stopIndex;

      // Lazy-attach the scroll listener once react-window's inner
      // element is mounted (first onRowsRendered call guarantees it).
      if (!listenersAttachedRef.current) {
        const el = listRef.current?.element;
        if (el) {
          listenersAttachedRef.current = true;
          lastScrollTopRef.current = el.scrollTop;
          const onScroll = () => {
            const cur = el.scrollTop;
            // Suppress one event right after a programmatic scroll. Our
            // scrollToRow only moves DOWN, so this is belt-and-suspenders;
            // it also stabilizes lastScrollTop so a small data-shift
            // micro-adjust doesn't look like user input.
            if (ignoreScrollRef.current) {
              ignoreScrollRef.current = false;
              lastScrollTopRef.current = cur;
              return;
            }
            const last = lastScrollTopRef.current;
            lastScrollTopRef.current = cur;
            // A genuine user scroll means they've taken control of the
            // viewport — clear the match-jump pin so the at-tail
            // auto-resume works normally if they scroll back to the tail.
            manualJumpRef.current = false;
            // 10px threshold ignores sub-pixel jitter from layout
            // adjustments. Programmatic scrolls never decrease scrollTop
            // because the tail-follow path only moves downward, so any
            // genuine decrease is user input (wheel up, scrollbar drag,
            // touch swipe).
            if (cur < last - 10 && isAutoScrollRef.current) {
              setIsAutoScroll(false);
            }
          };
          el.addEventListener('scroll', onScroll, { passive: true });
          cleanupListenersRef.current = () => {
            el.removeEventListener('scroll', onScroll);
          };
        }
      }

      if (isLive && !isAutoScroll) {
        // Resume detection only — the OFF transition lives in the scroll
        // listener so it's not subject to onRowsRendered's stale-stopIndex
        // races during data bursts.
        const atTail = stopIndex >= autoscrollIndex - TAIL_RESUME_TOLERANCE;
        if (atTail && !manualJumpRef.current) {
          setIsAutoScroll(true);
          ignoreScrollRef.current = true;
          listRef.current?.scrollToRow({
            index: autoscrollIndex,
            align: 'end',
            behavior: 'instant',
          });
        }
      }

    },
    [isLive, isAutoScroll, autoscrollIndex, listRef],
  );

  // Cleanup scroll listener on unmount.
  useEffect(() => {
    return () => {
      cleanupListenersRef.current?.();
      listenersAttachedRef.current = false;
    };
  }, []);

  // After a load-older completes, extraLogs grows by N. Those N entries
  // sort to the oldest end (top) of the list, which shifts every other
  // row down by N indices. Without an explicit scroll adjust the user
  // appears to jump backward in time by N rows. Re-anchor to where they
  // were so the new content fills above their viewport, not inside it.
  useEffect(() => {
    const newLen = extraLogs?.length ?? 0;
    const oldLen = prevExtraLogsLenRef.current;
    prevExtraLogsLenRef.current = newLen;
    if (newLen > oldLen && lastStartIndexRef.current > 0) {
      const delta = newLen - oldLen;
      ignoreScrollRef.current = true;
      listRef.current?.scrollToRow({
        index: lastStartIndexRef.current + delta,
        align: 'start',
        behavior: 'instant',
      });
    }
  }, [extraLogs, listRef]);

  // Reset autoscroll to "on" whenever live mode toggles off → on (next
  // Start should tail-follow). handleRowsRendered then re-evaluates on
  // the next scroll/render and only keeps it on while the user is at
  // the tail.
  useEffect(() => {
    if (!isLive) {
      queueMicrotask(() => setIsAutoScroll(true));
    }
  }, [isLive]);

  // Position of the selected row within the match list (1-based shown in
  // the toolbar as "3/12"); -1 when the selection isn't on a match.
  const currentMatchPos = selectedIndex != null ? matchIndices.indexOf(selectedIndex) : -1;

  const selectedLog = selectedIndex !== null ? displayLogs[selectedIndex] : null;
  const listHeight = showDetail && selectedLog
    ? height - 28 - detailHeight - 6
    : height - 28;

  // Keep the latest displayLogs in a ref so the row accessor callback is
  // referentially stable. Stable callback + small primitive props =
  // tiny memoizedProps per row → the React 19 dev profiler's
  // structured-clone diff stays bounded regardless of how many rows we
  // hold. This is the load-bearing fix that lets the buffer be unbounded.
  const displayLogsRef = useRef(displayLogs);
  displayLogsRef.current = displayLogs;
  const getLog = useCallback(
    (i: number): DeviceLog | undefined => displayLogsRef.current[i],
    [],
  );

  const rowProps: LogRowProps = useMemo(
    () => ({
      getLog, selectedIndex, markedIds,
      onSelect: handleSelect, onToggleMark: toggleMark, searchRegex,
    }),
    [getLog, selectedIndex, markedIds, handleSelect, toggleMark, searchRegex]
  );

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder={isRegex ? 'Regex pattern...' : 'Search logs...'}
          size="small" value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            // Enter jumps to the next match, Shift+Enter to the previous —
            // browser find-bar muscle memory.
            if (e.key === 'Enter') {
              e.preventDefault();
              navigateMatches(e.shiftKey ? 'prev' : 'next');
            }
          }}
          sx={{ flex: 1, minWidth: 150, maxWidth: 320 }}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: 0.25 }}>
                  {search && <IconButton size="small" onClick={() => setSearch('')}><ClearIcon sx={{ fontSize: 14 }} /></IconButton>}
                  <Tooltip title="Toggle regex search">
                    <ToggleButton
                      value="regex" selected={isRegex} onChange={() => setIsRegex(!isRegex)} size="small"
                      sx={{ border: 0, p: 0.25, fontSize: 10, fontWeight: 700, fontFamily: 'monospace', minWidth: 24, lineHeight: 1, color: isRegex ? colors.primary.main : 'text.disabled' }}
                    >.*</ToggleButton>
                  </Tooltip>
                </InputAdornment>
              ),
              sx: { fontSize: 12 },
            },
          }}
        />

        {!hideLevelFilter && (
          <Select displayEmpty size="small" value={levelFilter} onChange={(e) => setLevelFilter(e.target.value as LogLevel | '')} sx={{ minWidth: 90, fontSize: 12 }}>
            <MenuItem value="">All Levels</MenuItem>
            <MenuItem value="debug">Debug</MenuItem>
            <MenuItem value="info">Info</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="error">Error</MenuItem>
            <MenuItem value="fatal">Fatal</MenuItem>
          </Select>
        )}

        {!hideTypeFilter && (
          <Select displayEmpty size="small" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as LogType | '')} sx={{ minWidth: 90, fontSize: 12 }}>
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="system">System</MenuItem>
            <MenuItem value="app">App</MenuItem>
            <MenuItem value="crash">Crash</MenuItem>
            <MenuItem value="network">Network</MenuItem>
            <MenuItem value="security">Security</MenuItem>
            <MenuItem value="command">Command</MenuItem>
          </Select>
        )}

        {markedIds.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}>
            <Tooltip title="Previous bookmark"><IconButton size="small" onClick={() => navigateMarks('prev')}><PrevMarkIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            <Typography sx={{ fontSize: 11, color: colors.primary.main, fontWeight: 600 }}>{markedIds.size}</Typography>
            <Tooltip title="Next bookmark"><IconButton size="small" onClick={() => navigateMarks('next')}><NextMarkIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 'auto' }}>
          {debouncedSearch && (
            <>
              <Typography sx={{ fontSize: 11, color: matchCount ? colors.primary.main : 'text.disabled' }}>
                {matchCount > 0 && currentMatchPos >= 0
                  ? `${currentMatchPos + 1}/${matchCount}`
                  : `${matchCount} match${matchCount !== 1 ? 'es' : ''}`}
              </Typography>
              {matchCount > 0 && (
                <>
                  <Tooltip title="Previous match (Shift+Enter)">
                    <IconButton size="small" onClick={() => navigateMatches('prev')}>
                      <PrevMarkIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Next match (Enter)">
                    <IconButton size="small" onClick={() => navigateMatches('next')}>
                      <NextMarkIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Main panel */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
        <ColumnHeader />

        {displayLogs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
            <Typography variant="body2">No logs to display</Typography>
          </Box>
        ) : (
          <List
            listRef={listRef}
            rowComponent={LogRowRenderer}
            rowCount={displayLogs.length}
            rowHeight={ROW_HEIGHT}
            rowProps={rowProps}
            overscanCount={20}
            onRowsRendered={handleRowsRendered}
            style={{ height: Math.max(listHeight, 100) }}
          />
        )}

        {showDetail && selectedLog && (
          <>
            <ResizeHandle onDrag={handleResize} />
            <Box sx={{ height: detailHeight, overflow: 'hidden' }}>
              <DetailPanel log={selectedLog} onClose={handleCloseDetail} />
            </Box>
          </>
        )}

      </Paper>
    </Box>
  );
}

export default LogViewer;
